import { LocalProfile } from "@core/model/local-profile";
import { LocalRole } from "@core/model/local-role.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { PROFILE } from "@core/utility/settings";
import StorageService from "@shared/services/storage.service";
import SimpleHeader from "@shared/ui/navigation/simple-header/simple-header";
import SimpleSidebar, {
  SimpleMenu,
} from "@shared/ui/navigation/simple-sidebar/simple-sidebar";
import { OverlayPanel } from "primereact/overlaypanel";
import { Sidebar } from "primereact/sidebar";
import { useCallback, useRef, useState } from "react";
import { BiSolidUserAccount } from "react-icons/bi";
import { FaBuilding, FaUserCircle, FaUsers } from "react-icons/fa";
import { FaCalendar, FaUserPlus } from "react-icons/fa6";
import { FaRegFileLines } from "react-icons/fa6";
import { IoMdBulb } from "react-icons/io";
import {
  MdAccountCircle,
  MdAccountTree,
  MdBusinessCenter,
  MdDashboard,
  MdOutlinePayment,
} from "react-icons/md";
import { RiShutDownLine } from "react-icons/ri";
import { RiContractFill } from "react-icons/ri";
import { RxHamburgerMenu } from "react-icons/rx";
import { NavLink, useNavigate } from "react-router-dom";

import { appConfig } from "../../../app.config";
import { appFeature } from "../../../app.feature";

const getLocalProfileFromStorage = () => {
  const profile = StorageService.load<LocalProfile>(PROFILE);
  return profile || null;
};

export function useAuthShellNavigation() {
  const navigate = useNavigate();
  const { getUserRole, getAuthUser } = useUserIdentityContext();
  const slug = getAuthUser().slug;
  const logo = `${
    appConfig.bucketAssetPath
  }/${slug}/icon-192x192.png?${new Date().toISOString()}`;
  const profile = getLocalProfileFromStorage();
  const [mobileMenu, setMobileMenu] = useState(false);
  const overlayRef = useRef(null);
  const domainNavigation = [
    {
      title: "Dashboard",
      icon: <MdDashboard />,
      path: "",
    },
    {
      title: "Patients",
      icon: <FaUserPlus />,
      path: "patient",
    },
    {
      title: "Contract",
      icon: <RiContractFill />,
      path: "contract",
    },
    {
      title: "Appointments",
      icon: <FaCalendar />,
      path: "registration",
    },
    {
      title: "Service",
      icon: <IoMdBulb />,
      path: "util-service",
    },
    {
      title: "Payment Provider",
      icon: <MdOutlinePayment />,
      path: "provider-type",
    },
    {
      title: "Waiver",
      icon: <FaRegFileLines />,
      path: "waiver",
    },
    {
      title: "Account",
      icon: <FaUsers />,
      path: "account",
    },
  ];

  const getDomainNavigation = useCallback(() => {
    return domainNavigation;
  }, []);

  const getAccountNavigation = useCallback(() => {
    const userCode = getAuthUser()?.userCode;
    let accountNavigation = [
      {
        title: "My Account",
        icon: <FaUserCircle />,
        path: `account/${userCode}`,
      },
    ];

    const userRole = getUserRole();

    if (userRole.name.toUpperCase() === LocalRole.Admin) {
      accountNavigation = [
        ...accountNavigation,
        {
          title: "Tenant",
          icon: <FaBuilding />,
          path: "tenant",
        },
        {
          title: "Account",
          icon: <BiSolidUserAccount />,
          path: "account",
        },
        {
          title: "Role",
          icon: <MdAccountTree />,
          path: "role",
        },
      ];
    }

    if (userRole?.name.toUpperCase() === LocalRole.Manager) {
      accountNavigation = [
        ...accountNavigation,
        {
          title: "My Business",
          icon: <MdBusinessCenter />,
          path: `tenant/${userRole.tenant}`,
        },
        {
          title: "Account",
          icon: <MdAccountCircle />,
          path: "account",
        },
      ];
    }
    return accountNavigation;
  }, [getUserRole]);
  const handleLogout = () => {
    navigate("/login");
  };
  const handleMobileMenu = () => {
    setMobileMenu(true);
  };

  const headerElement = (
    <SimpleHeader
      title={profile?.businessName}
      logo={logo}
      style={appFeature.theme.header}
    >
      <section
        className="flex gap-2 items-center p-2 rounded-lg cursor-pointer border border-transparent hover:border-gray-50 hover:bg-white"
        onClick={(e) => (overlayRef as any).current.toggle(e)}
      >
        <span className="text-2xl">
          <FaUserCircle />
        </span>
        <div className="flex flex-col">
          <span className="text-base font-bold">
            {profile?.name || "System"}
          </span>
          <span className="text-xs">{profile?.roleName || "Admin"}</span>
        </div>
      </section>
      <OverlayPanel ref={overlayRef}>
        {getAccountNavigation().map((item, id) => (
          <NavLink
            to={item.path}
            key={id}
            className="flex items-center gap-2 p-2 cursor-pointer rounded-lg hover:bg-gray-100 hover:text-green-800"
            onClick={(e) => (overlayRef as any).current.toggle(e)}
          >
            <span className="text-base">{item.icon}</span>
            <span>{item.title}</span>
          </NavLink>
        ))}
      </OverlayPanel>

      <section className="hidden md:inline-flex">
        <button
          className="flex gap-2 items-center hover:bg-gray-50 hover:bg-opacity-40 p-2 rounded-lg border border-gray-600"
          onClick={() => handleLogout()}
        >
          <span className="text-2xl">
            <RiShutDownLine />
          </span>
        </button>
      </section>

      <section className="inline-flex md:hidden">
        <button
          className="flex gap-2 items-center hover:bg-gray-50 hover:bg-opacity-40 p-2 rounded-lg border border-gray-600"
          onClick={() => handleMobileMenu()}
        >
          <span className="text-2xl">
            <RxHamburgerMenu />
          </span>
        </button>
      </section>
    </SimpleHeader>
  );

  const navigationElement = (
    <div id="AuthShellNavigation" className="auth-shell-navigation">
      {/* Desktop Navigation */}
      <SimpleSidebar hostClass="hidden md:inline-flex">
        {getDomainNavigation().map((item, id) => (
          <SimpleMenu
            key={id}
            title={item.title}
            icon={item.icon}
            path={item.path}
          />
        ))}
      </SimpleSidebar>

      {/* Mobile Navigation */}
      <Sidebar
        visible={mobileMenu}
        onHide={() => setMobileMenu(false)}
        fullScreen
      >
        <header className="flex w-full justify-center">
          <div className="flex items-center gap-2">
            <h2>{profile?.businessName}</h2>
          </div>
        </header>

        <small className="block ml-4 mt-4">Menu</small>
        {getDomainNavigation().map((item, id) => (
          <SimpleMenu
            key={id}
            title={item.title}
            icon={item.icon}
            path={item.path}
          />
        ))}
        <SimpleMenu title="Logout" icon={<RiShutDownLine />} />
      </Sidebar>
    </div>
  );

  return {
    headerElement,
    navigationElement,
  };
}
